import React from 'react';
import { Icons as I } from './pack-icons';

/**
 * Generic confirmation dialog used across the app.
 *
 * Props:
 *  - open: boolean
 *  - tone: 'info' | 'warn' | 'danger' (icon + accent)
 *  - icon: optional ReactNode shown instead of the tone icon
 *  - title: string
 *  - description: string | ReactNode
 *  - confirmLabel: string (default "Confirm")
 *  - confirmingLabel: string (default "Working...")
 *  - cancelLabel: string (default "Cancel")
 *  - confirming: boolean (button disabled + label swap)
 *  - confirmDisabled: boolean (button disabled while prerequisite data loads)
 *  - onConfirm: () => void
 *  - onCancel: () => void
 *  - children: optional extra content (e.g. proration summary)
 */
export function ConfirmDialog({
  open,
  tone = 'info',
  icon = null,
  title,
  description,
  confirmLabel = 'Confirm',
  confirmingLabel = 'Working...',
  cancelLabel = 'Cancel',
  confirming = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
  children,
}) {
  if (!open) return null;

  const IconCmp = tone === 'danger'
    ? I.alert
    : tone === 'warn'
      ? I.info
      : I.sparkles;

  const confirmClass = tone === 'danger'
    ? 'btn btn-danger'
    : tone === 'warn'
      ? 'btn btn-violet'
      : 'btn btn-primary';

  return (
    <div className="modal-scrim" role="presentation" onClick={confirming ? undefined : onCancel}>
      <div
        className="modal card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 460 }}
      >
        <div className="type-icon" aria-hidden="true">
          {icon || <IconCmp size={18} />}
        </div>
        <h3 id="confirm-dialog-title" className="h3" style={{ marginTop: 8 }}>{title}</h3>
        {description && (
          <div className="body-sm" style={{ color: 'var(--text-2)', marginTop: 6, lineHeight: 1.5 }}>
            {description}
          </div>
        )}
        {children && <div style={{ marginTop: 12 }}>{children}</div>}
        <div className="row gap-8" style={{ marginTop: 18, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={confirming}>
            {cancelLabel}
          </button>
          <button type="button" className={confirmClass} onClick={onConfirm} disabled={confirming || confirmDisabled}>
            {confirming ? confirmingLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmDialog;
